/**
 * Nudges System - Matches Chrome Extension Behavior
 * Shows contextual alerts and nudges at timed intervals during demo
 * Based on /chrome-extension/src/features/status-bar/nudges.js
 */

export const NUDGE_SCHEDULE = [
  { 
    minutes: 5, 
    severity: 'excellent', 
    title: '5-Min Momentum Boost',
    message: 'Nice momentum! Quick spotlight to keep everything crisp:',
    checklist: [
      'Confirm demographics or insurance updates are captured',
      'Skim meds and allergies for anything new or risky',
      'Scan for priority flags before diving deeper',
      'Make sure the visit reason matches your chart focus'
    ]
  },
  { 
    minutes: 10, 
    severity: 'info', 
    title: 'Midpoint Confidence Check',
    message: "You're building a strong story. Midpoint nudge:",
    checklist: [
      'Capture the primary concern in your own words',
      'Log vitals or key observations you gathered',
      'Document barriers or social factors you uncovered',
      "Knock out required screenings while they're top of mind"
    ]
  },
  { 
    minutes: 15, 
    severity: 'warning', 
    title: 'Finish-Line Focus',
    message: "You're in the groove - let's set you up for the finish:",
    checklist: [
      'Capture interventions and escalations you handled',
      'Queue up referrals or follow-ups before you forget',
      "Summarize today's outcomes in coaching notes",
      'Make sure nothing important is still in draft mode'
    ]
  },
  { 
    minutes: 20, 
    severity: 'warning', 
    title: 'Wrap-Up Power Moves',
    message: 'Time to land the plane while the energy is high:',
    checklist: [
      'Give your note one last read for clarity and completeness',
      'Save/sign so the timer reflects your pace',
      'Close out any tasks tied to this encounter',
      'Spot-check anything your team lead highlighted this week'
    ]
  },
  { 
    minutes: 25, 
    severity: 'error', 
    title: 'Backup On Standby',
    message: "You've invested a ton already - let's call in backup if you need it:",
    checklist: [
      'Save everything so no work is lost',
      'Jot down blockers so we can clear them later',
      "Close the encounter if you're ready to move forward",
      "Tap 'Ask for Help' and we'll loop in your team lead"
    ]
  }
];

class NudgesSystem {
  constructor() {
    this.nudges = [];
    this.shownNudges = new Set();
    this.activeModal = null;
  }

  /**
   * Check if it's time to show a nudge based on session time
   * @param {number} sessionTime - Session time in seconds
   */
  checkAndShowNudge(sessionTime) {
    const minutes = Math.floor(sessionTime / 60);
    
    // Find nudge that matches this time
    const nudge = NUDGE_SCHEDULE.find(n => n.minutes === minutes);
    
    if (nudge && !this.shownNudges.has(nudge.minutes)) {
      this.shownNudges.add(nudge.minutes);
      this.showNudgeModal(nudge);
      return true;
    }
    
    return false;
  }

  /**
   * Show centered nudge modal (matches Chrome extension styling)
   * @param {Object} nudge - Nudge data
   */
  showNudgeModal(nudge) {
    // Remove existing modal if any
    if (this.activeModal) {
      this.activeModal.remove();
      this.activeModal = null;
    }

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'prism-nudge-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      animation: fadeIn 0.3s ease-out;
    `;

    // Create modal container with gradient border
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: relative;
      border-radius: 20px;
      max-width: 520px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(12, 20, 49, 0.5);
      animation: slideInUp 0.3s ease-out;
      padding: 8px;
      background: linear-gradient(135deg, rgba(108, 125, 187, 1) 0%, rgba(172, 128, 202, 1) 50%, rgba(134, 189, 189, 1) 100%);
    `;

    // Inner content with semi-transparent white background
    const content = document.createElement('div');
    content.style.cssText = `
      background: rgba(255, 255, 255, 0.95);
      border-radius: 20px;
      padding: 36px;
    `;

    // Build checklist HTML
    const checklistHTML = nudge.checklist && nudge.checklist.length > 0
      ? `
        <div style="margin-top: 20px; padding: 20px; background: rgba(12, 20, 49, 0.05); border-radius: 12px; border: 2px solid rgba(12, 20, 49, 0.1);">
          <div style="color: rgba(12, 20, 49, 1); font-size: 15px; font-weight: 700; margin-bottom: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            Quick Checklist:
          </div>
          ${nudge.checklist.map(item => `
            <div style="color: rgba(12, 20, 49, 0.9); font-size: 14px; line-height: 2; padding-left: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              • ${this.escapeHTML(item)}
            </div>
          `).join('')}
        </div>
      `
      : '';

    content.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 16px;">
        <div style="text-align: center;">
          <div style="color: rgba(12, 20, 49, 1); font-size: 24px; font-weight: 800; margin-bottom: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            ${this.escapeHTML(nudge.title || 'Chart Progress Update')}
          </div>
          <div style="color: rgba(12, 20, 49, 0.8); font-size: 16px; line-height: 1.6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            ${this.escapeHTML(nudge.message)}
          </div>
        </div>
        ${checklistHTML}
        <div style="display: flex; justify-content: center; gap: 12px; margin-top: 12px; flex-wrap: wrap;">
          <button id="prism-nudge-help-btn" style="
            background: rgba(12, 20, 49, 0.9);
            color: #ffffff;
            border: 3px solid rgba(108, 125, 187, 0.8);
            padding: 12px 22px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 800;
            cursor: pointer;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            transition: all 0.2s;
            box-shadow: 0 10px 20px rgba(12, 20, 49, 0.25);
          ">
            Ask for Help
          </button>
          <button id="prism-nudge-modal-dismiss" style="
            background: transparent;
            color: rgba(108, 125, 187, 0.8);
            border: 3px solid rgba(108, 125, 187, 0.8);
            padding: 12px 28px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 800;
            cursor: pointer;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            transition: all 0.2s;
          ">
            Got it!
          </button>
        </div>
      </div>
    `;

    // Add hover effects
    const setupButtonHovers = () => {
      const helpBtn = content.querySelector('#prism-nudge-help-btn');
      if (helpBtn) {
        helpBtn.addEventListener('mouseenter', () => {
          helpBtn.style.background = 'rgba(12, 20, 49, 0.7)';
        });
        helpBtn.addEventListener('mouseleave', () => {
          helpBtn.style.background = 'rgba(12, 20, 49, 0.9)';
        });
      }

      const dismissBtn = content.querySelector('#prism-nudge-modal-dismiss');
      if (dismissBtn) {
        dismissBtn.addEventListener('mouseenter', () => {
          dismissBtn.style.background = 'rgba(108, 125, 187, 0.3)';
        });
        dismissBtn.addEventListener('mouseleave', () => {
          dismissBtn.style.background = 'transparent';
        });
      }
    };

    modal.appendChild(content);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    
    // Setup button interactions after appending to DOM
    setupButtonHovers();

    // Add dismiss handler
    const dismissBtn = content.querySelector('#prism-nudge-modal-dismiss');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        overlay.style.animation = 'fadeOut 0.2s ease-out';
        setTimeout(() => {
          overlay.remove();
          this.activeModal = null;
        }, 200);
      });
    }

    // Add help handler
    const helpBtn = content.querySelector('#prism-nudge-help-btn');
    if (helpBtn) {
      helpBtn.addEventListener('click', () => {
        helpBtn.disabled = true;
        helpBtn.textContent = '✓ Help requested!';
        helpBtn.style.background = 'rgba(91, 158, 130, 0.9)';
        
        setTimeout(() => {
          overlay.style.animation = 'fadeOut 0.2s ease-out';
          setTimeout(() => {
            overlay.remove();
            this.activeModal = null;
          }, 200);
        }, 1500);
      });
    }

    // Click outside to dismiss
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.style.animation = 'fadeOut 0.2s ease-out';
        setTimeout(() => {
          overlay.remove();
          this.activeModal = null;
        }, 200);
      }
    });

    // Add animations if not already present
    if (!document.getElementById('prism-nudge-animations')) {
      const style = document.createElement('style');
      style.id = 'prism-nudge-animations';
      style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes slideInUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }

    this.activeModal = overlay;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Get nudge count for a given session time
   * @param {number} sessionTime - Session time in seconds
   * @returns {number} Number of nudges that should be shown
   */
  getNudgeCount(sessionTime) {
    const minutes = Math.floor(sessionTime / 60);
    return NUDGE_SCHEDULE.filter(n => n.minutes <= minutes).length;
  }

  /**
   * Reset nudges history (for demo replay)
   */
  reset() {
    this.shownNudges.clear();
    if (this.activeModal) {
      this.activeModal.remove();
      this.activeModal = null;
    }
  }
}

// Export singleton instance
const nudgesSystem = new NudgesSystem();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.prismNudges = nudgesSystem;
}

export default nudgesSystem;
